import React, { useEffect, useMemo, useState } from 'react';

/**
 * Packs : ventes ponctuelles (04/10/2026).
 *
 * Un pack s'achète une fois et crédite UN quota du catalogue :
 *
 *  - CRÉDITS  : un stock (emails, messages d'assistant IA…), consommé une fois
 *               le quota du mois de la formule épuisé ;
 *  - CAPACITÉ : un plafond relevé (dossiers, stockage, utilisateurs) tant que
 *               le pack est valide.
 *
 * La validité se règle par pack ; vide, elle est sans limite. Un pack se vend
 * avec un prix Stripe PONCTUEL (one-time), rattaché ici et jamais créé ici —
 * comme les formules. Ce qui a été vendu est figé à l'achat : modifier un
 * pack ne change que les ventes suivantes.
 */

const NATURES = [
  { id: 'CREDITS', label: 'Crédits', aide: 'Stock consommé une fois le quota du mois épuisé.' },
  { id: 'CAPACITE', label: 'Capacité', aide: 'Plafond relevé tant que le pack est valide.' },
];

const VIDE = {
  id: null,
  name: '',
  description: '',
  featureId: '',
  nature: 'CREDITS',
  quantite: '100',
  validiteMois: '',
  prix: '',
  stripePriceId: '',
  active: false,
  position: '100',
  reserveFormulePayante: false,
};

// Grille tarifaire (19/10/2026) : les packs de mails ne s'achètent pas en
// Free. Proposé coché à la création d'un pack sur ce quota, modifiable.
const RESERVES_PAYANT_PAR_DEFAUT = ['app.quota.mailing_month'];

const natureParDefaut = (cle) => (/_(month|mois)$/.test(cle ?? '') ? 'CREDITS' : 'CAPACITE');

const euros = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const nombre = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0);
const date = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

/** État d'un achat : remboursé (bloqué), échu, ou en cours. */
function etatAchat(a) {
  if (a.rembourseLe)
    return `remboursé le ${date(a.rembourseLe)}${a.montantRembourse != null ? ` (${euros(a.montantRembourse)})` : ''}`;
  if (a.expireLe && new Date(a.expireLe) <= new Date()) return `échu le ${date(a.expireLe)}`;
  return a.expireLe ? `valable jusqu’au ${date(a.expireLe)}` : 'en cours';
}

export function AdminPacks({ call, products, features, stripePrices, canWrite }) {
  const [product, setProduct] = useState(products[0]?.id ?? 'APP');
  const [packs, setPacks] = useState([]);
  const [form, setForm] = useState(null);
  const [offre, setOffre] = useState(null);
  // Achats d'un pack (04/10/2026) : { pack, liste } ; liste null = chargement.
  const [achats, setAchats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let vivant = true;
    call(`/admin/packs?product=${product}`)
      .then((liste) => {
        if (!vivant) return;
        setPacks(liste);
        setError('');
      })
      .catch((e) => {
        if (vivant) setError(String(e.message ?? e));
      });
    return () => {
      vivant = false;
    };
  }, [call, product]);

  const quotas = useMemo(
    () => features.filter((f) => f.product === product && f.kind === 'QUOTA'),
    [features, product],
  );
  // Prix PONCTUELS seulement, libres — ou celui du pack en cours d'édition.
  const prixPonctuels = useMemo(
    () =>
      stripePrices.filter(
        (p) => p.type === 'one_time' && (!p.linked || p.id === form?.stripePriceId),
      ),
    [stripePrices, form?.stripePriceId],
  );

  const changerProduit = (id) => {
    setProduct(id);
    setForm(null);
    setOffre(null);
    setAchats(null);
  };

  const voirAchats = async (pack) => {
    setForm(null);
    setOffre(null);
    setAchats({ pack, liste: null });
    try {
      const liste = await call(`/admin/packs/${pack.id}/achats`);
      setAchats({ pack, liste });
    } catch (e) {
      setError(String(e.message ?? e));
      setAchats(null);
    }
  };

  // Remboursement Stripe : seulement un pack qui n'a pas servi (crédits) ; un
  // pack de capacité toujours, son plafond redescend. L'achat est bloqué.
  const rembourser = (achat) => {
    const capacite = achat.nature === 'CAPACITE';
    const avertissement = capacite
      ? 'Le plafond de l’espace redescend : ce qui existe est gardé, mais plus rien de nouveau au-delà.'
      : 'Les crédits de ce pack ne pourront plus être utilisés.';
    if (
      !window.confirm(
        `Rembourser ${euros(achat.montant)} à « ${achat.espace.nom} » ? ${avertissement}`,
      )
    )
      return;
    agir(
      () => call(`/admin/packs/achats/${achat.id}/rembourser`, { method: 'POST' }),
      `Achat remboursé (${euros(achat.montant)}) : il est bloqué.`,
    ).then((ok) => ok && voirAchats(achats.pack));
  };

  async function agir(action, message) {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await action();
      setPacks(await call(`/admin/packs?product=${product}`));
      setNotice(message);
      return true;
    } catch (e) {
      setError(String(e.message ?? e));
      return false;
    } finally {
      setSaving(false);
    }
  }

  const editer = (pack) => {
    setOffre(null);
    setAchats(null);
    setForm({
      id: pack.id,
      name: pack.name,
      description: pack.description ?? '',
      featureId: pack.feature.id,
      nature: pack.nature,
      quantite: String(pack.quantite),
      validiteMois: pack.validiteMois == null ? '' : String(pack.validiteMois),
      prix: String(pack.prix),
      stripePriceId: pack.stripePriceId ?? '',
      active: pack.active,
      position: String(pack.position),
      reserveFormulePayante: Boolean(pack.reserveFormulePayante),
    });
  };

  const choisirQuota = (featureId) => {
    const f = quotas.find((q) => q.id === featureId);
    setForm({
      ...form,
      featureId,
      nature: form.id ? form.nature : natureParDefaut(f?.key),
      reserveFormulePayante: form.id
        ? form.reserveFormulePayante
        : RESERVES_PAYANT_PAR_DEFAUT.includes(f?.key),
    });
  };

  const enregistrer = () => {
    const corps = {
      product,
      name: form.name.trim(),
      description: form.description.trim() || null,
      featureId: form.featureId,
      nature: form.nature,
      quantite: Number(form.quantite),
      validiteMois: form.validiteMois === '' ? null : Number(form.validiteMois),
      prix: Number(String(form.prix).replace(',', '.')),
      stripePriceId: form.stripePriceId.trim() || null,
      active: form.active,
      position: Number(form.position) || 100,
      reserveFormulePayante: form.reserveFormulePayante,
    };
    return agir(
      () =>
        call(form.id ? `/admin/packs/${form.id}` : '/admin/packs', {
          method: form.id ? 'PATCH' : 'POST',
          body: JSON.stringify(corps),
        }),
      form.id ? `Pack « ${corps.name} » enregistré.` : `Pack « ${corps.name} » créé.`,
    ).then((ok) => ok && setForm(null));
  };

  const offrir = () =>
    agir(
      () =>
        call(`/admin/packs/${offre.pack.id}/offrir`, {
          method: 'POST',
          body: JSON.stringify({ email: offre.email.trim(), nombre: Number(offre.nombre) || 1 }),
        }),
      `« ${offre.pack.name} » offert à ${offre.email.trim()}.`,
    ).then((ok) => ok && setOffre(null));

  const supprimer = (pack) => {
    if (
      !window.confirm(
        `Supprimer le pack « ${pack.name} » ? (Possible seulement s’il n’a jamais été acheté.)`,
      )
    )
      return;
    agir(
      () => call(`/admin/packs/${pack.id}`, { method: 'DELETE' }),
      `Pack « ${pack.name} » supprimé.`,
    );
  };

  const quotaChoisi = quotas.find((q) => q.id === form?.featureId);
  const incomplet =
    !form?.name.trim() ||
    !form?.featureId ||
    !(Number(form?.quantite) >= 1) ||
    form?.prix === '' ||
    Number.isNaN(Number(String(form?.prix).replace(',', '.')));

  return (
    <div className="ap-promo">
      <div className="ap-promo-head">
        <label>
          Produit{' '}
          <select value={product} onChange={(e) => changerProduit(e.target.value)}>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        {canWrite && (
          <button
            className="ap-btn primary"
            disabled={saving || quotas.length === 0}
            onClick={() => {
              setOffre(null);
              setAchats(null);
              setForm(form ? null : { ...VIDE });
            }}
          >
            {form ? 'Annuler' : '+ Nouveau pack'}
          </button>
        )}
      </div>

      {error && <div className="ap-error">{error}</div>}
      {notice && <div className="ap-notice">{notice}</div>}
      <p className="ap-muted small">
        Un pack crédite un <strong>quota</strong> du produit. Les crédits se consomment une fois le
        quota du mois de la formule épuisé ; une capacité relève le plafond tant que le pack est
        valide. Il se vend avec un prix Stripe <strong>ponctuel</strong> (pas récurrent), créé dans
        Stripe puis rattaché ici.
      </p>
      {quotas.length === 0 && (
        <p className="ap-muted">
          Aucun quota pour ce produit : créez d’abord une fonctionnalité de type Quota.
        </p>
      )}

      {form && (
        <section className="ap-panel">
          <h3>{form.id ? 'Modifier le pack' : 'Nouveau pack'}</h3>

          <label>
            Nom affiché au client
            <input
              value={form.name}
              maxLength={150}
              placeholder="Pack 1 000 emails"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            Description (facultative)
            <input
              value={form.description}
              maxLength={500}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <label>
            Quota crédité
            <select value={form.featureId} onChange={(e) => choisirQuota(e.target.value)}>
              <option value="">— Choisir —</option>
              {quotas.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.label} ({q.key})
                </option>
              ))}
            </select>
          </label>

          <div className="ap-promo-natures">
            {NATURES.map((n) => (
              <label key={n.id}>
                <input
                  type="radio"
                  checked={form.nature === n.id}
                  onChange={() => setForm({ ...form, nature: n.id })}
                />{' '}
                {n.label}
              </label>
            ))}
          </div>
          <p className="ap-muted small">{NATURES.find((n) => n.id === form.nature)?.aide}</p>

          <label>
            Quantité par pack{quotaChoisi ? ` (${quotaChoisi.label})` : ''}
            <input
              type="number"
              min={1}
              value={form.quantite}
              onChange={(e) => setForm({ ...form, quantite: e.target.value })}
            />
          </label>
          <label>
            Validité en mois (vide : sans limite)
            <input
              type="number"
              min={1}
              max={120}
              value={form.validiteMois}
              placeholder="Sans limite"
              onChange={(e) => setForm({ ...form, validiteMois: e.target.value })}
            />
          </label>
          <label>
            Prix affiché (€ TTC)
            <input
              inputMode="decimal"
              value={form.prix}
              placeholder="9,90"
              onChange={(e) => setForm({ ...form, prix: e.target.value })}
            />
          </label>
          <label>
            Prix Stripe ponctuel
            {prixPonctuels.length > 0 ? (
              <select
                value={form.stripePriceId}
                onChange={(e) => setForm({ ...form, stripePriceId: e.target.value })}
              >
                <option value="">— Aucun (pack non vendable) —</option>
                {prixPonctuels.map((p) => (
                  <option key={p.id} value={p.id}>
                    {[p.productName, p.nickname, p.amount != null ? euros(p.amount) : null]
                      .filter(Boolean)
                      .join(' · ')}{' '}
                    ({p.id})
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={form.stripePriceId}
                placeholder="price_…"
                onChange={(e) => setForm({ ...form, stripePriceId: e.target.value })}
              />
            )}
          </label>
          <label>
            Ordre d’affichage
            <input
              type="number"
              min={0}
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            />
          </label>
          <label className="ap-check">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />{' '}
            En vente
          </label>
          <label className="ap-check">
            <input
              type="checkbox"
              checked={form.reserveFormulePayante}
              onChange={(e) => setForm({ ...form, reserveFormulePayante: e.target.checked })}
            />{' '}
            Réservé aux formules payantes
          </label>
          <p className="ap-muted small">
            Coché : le pack n’est ni proposé ni vendu à un espace en formule gratuite (il reste
            possible de l’offrir).
          </p>
          <p className="ap-muted small">
            Un pack n’est proposé aux clients que s’il est en vente ET rattaché à un prix Stripe.
          </p>

          <div className="ap-actions">
            <button className="ap-btn primary" disabled={saving || incomplet} onClick={enregistrer}>
              {saving ? 'Enregistrement…' : form.id ? 'Enregistrer' : 'Créer le pack'}
            </button>
            <button className="ap-btn" disabled={saving} onClick={() => setForm(null)}>
              Annuler
            </button>
          </div>
        </section>
      )}

      {offre && (
        <section className="ap-panel">
          <h3>Offrir « {offre.pack.name} »</h3>
          <p className="ap-muted small">
            Crédité à l’espace du compte, sans paiement. Utile pour un geste commercial — ou pour
            essayer les crédits sans passer par Stripe.
          </p>
          <label>
            Adresse du compte
            <input
              type="email"
              value={offre.email}
              onChange={(e) => setOffre({ ...offre, email: e.target.value })}
            />
          </label>
          <label>
            Nombre de packs
            <input
              type="number"
              min={1}
              max={100}
              value={offre.nombre}
              onChange={(e) => setOffre({ ...offre, nombre: e.target.value })}
            />
          </label>
          <div className="ap-actions">
            <button
              className="ap-btn primary"
              disabled={saving || !offre.email.trim()}
              onClick={offrir}
            >
              {saving ? 'Envoi…' : 'Offrir'}
            </button>
            <button className="ap-btn" disabled={saving} onClick={() => setOffre(null)}>
              Annuler
            </button>
          </div>
        </section>
      )}

      {achats && (
        <section className="ap-panel">
          <h3>Achats de « {achats.pack.name} »</h3>
          <p className="ap-muted small">
            Un pack de crédits ne se rembourse que s’il n’a pas servi ; un pack de capacité toujours
            (le plafond redescend). Remboursé, l’achat est bloqué — y compris quand le remboursement
            est fait directement dans Stripe.
          </p>
          {achats.liste === null ? (
            <p className="ap-muted">Chargement…</p>
          ) : achats.liste.length === 0 ? (
            <p className="ap-muted">Aucun achat.</p>
          ) : (
            <ul className="ap-promo-list">
              {achats.liste.map((a) => (
                <li key={a.id} className="ap-promo-item">
                  <div>
                    <div className="ap-promo-titre">
                      <strong>{a.espace.nom}</strong>
                      {a.espace.proprietaire && (
                        <span className="ap-muted small">{a.espace.proprietaire}</span>
                      )}
                      {a.source === 'OFFERT' && <span className="ap-tag muted">offert</span>}
                      {a.rembourseLe && <span className="ap-tag">remboursé</span>}
                    </div>
                    <p className="ap-muted small">
                      {[
                        `acheté le ${date(a.acheteLe)}`,
                        `${a.nombre} pack(s)`,
                        a.nature === 'CREDITS'
                          ? `${nombre(a.utilise)} / ${nombre(a.quantite)} utilisé(s)`
                          : `+${nombre(a.quantite)}`,
                        euros(a.montant),
                        etatAchat(a),
                      ].join(' · ')}
                    </p>
                    {!a.remboursable && !a.rembourseLe && a.motif && (
                      <p className="ap-muted small">{a.motif}</p>
                    )}
                  </div>
                  {canWrite && a.remboursable && (
                    <div className="ap-promo-actions">
                      <button className="ap-btn" disabled={saving} onClick={() => rembourser(a)}>
                        Rembourser
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="ap-actions">
            <button className="ap-btn" onClick={() => setAchats(null)}>
              Fermer
            </button>
          </div>
        </section>
      )}

      {packs.length === 0 ? (
        <p className="ap-muted">Aucun pack pour ce produit.</p>
      ) : (
        <ul className="ap-promo-list">
          {packs.map((pack) => (
            <li key={pack.id} className="ap-promo-item">
              <div>
                <div className="ap-promo-titre">
                  <strong>{pack.name}</strong>
                  <span className="ap-tag">
                    {pack.nature === 'CREDITS' ? 'crédits' : 'capacité'}
                  </span>
                  {!pack.active && <span className="ap-tag muted">hors vente</span>}
                  {pack.reserveFormulePayante && <span className="ap-tag">formules payantes</span>}
                  {pack.active && !pack.stripePriceId && (
                    <span className="ap-tag">sans prix Stripe</span>
                  )}
                </div>
                <p>
                  +{nombre(pack.quantite)} {pack.feature.label} —{' '}
                  <strong>{euros(pack.prix)}</strong>
                </p>
                <p className="ap-muted small">
                  {[
                    pack.feature.key,
                    pack.validiteMois
                      ? `valable ${pack.validiteMois} mois`
                      : 'sans limite de durée',
                    `${pack._count?.achats ?? 0} achat(s)`,
                  ].join(' · ')}
                </p>
                {pack.description && <p className="ap-muted small">{pack.description}</p>}
              </div>
              <div className="ap-promo-actions">
                {(pack._count?.achats ?? 0) > 0 && (
                  <button
                    className="ap-btn"
                    disabled={saving}
                    onClick={() => void voirAchats(pack)}
                  >
                    Achats
                  </button>
                )}
                {canWrite && (
                  <>
                    <button className="ap-btn" disabled={saving} onClick={() => editer(pack)}>
                      Modifier
                    </button>
                    <button
                      className="ap-btn"
                      disabled={saving}
                      onClick={() => {
                        setForm(null);
                        setAchats(null);
                        setOffre({ pack, email: '', nombre: '1' });
                      }}
                    >
                      Offrir
                    </button>
                    {(pack._count?.achats ?? 0) === 0 && (
                      <button className="ap-btn" disabled={saving} onClick={() => supprimer(pack)}>
                        Supprimer
                      </button>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AdminPacks;
